import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { ReplaySubject, Subscription, Subject } from 'rxjs';
import { SapiService, SapiQueryResponse } from './sapi.service';
import { NavigationStart, Router } from '@angular/router';
import { WebSocketsService } from './web-sockets.service';

export interface MediaclipJobDefinition {
	id?: string;
	name?: string;
	mediaformat?: string;
	src?: string;
}

export interface MediaclipJob {
	id?: string;
	mediajobs: JobDetails[];
	transcodingAverage?: any;
}

export interface JobDetails {
	status: 'new' | 'transcoded' | 'running' | 'error';
	timeleft: number;
	totaltime: number;
	mediajobdefid: string;
	mediaJobName?: string;
}

@Injectable()
export class TranscodingService {

	private mediaclipJobDefinitions: ReplaySubject<MediaclipJobDefinition[]>;
	private _jobsSubject: ReplaySubject< MediaclipJob[]>;
	private wsSubscription: Subscription;
	private mediaclipJobsArray: MediaclipJob[] = [];
	private renderer: Renderer2;

	constructor(
		private sapiService: SapiService,
		private router: Router,
		protected webSocketsService: WebSocketsService,
		private rendererFactory: RendererFactory2
	) {

		this.mediaclipJobDefinitions = new ReplaySubject<MediaclipJobDefinition[]>(1);
		this._jobsSubject = new ReplaySubject< MediaclipJob[] >(1);

		this.getJobs();

		this.sapiService.getUrl('/mediajobdef').subscribe((mediaclipJobs: SapiQueryResponse) => {
			if (mediaclipJobs && mediaclipJobs.items) {
				const mediaclipJobDefinitions = mediaclipJobs.items as MediaclipJobDefinition[];
				this.mediaclipJobDefinitions.next(mediaclipJobDefinitions);
			}
		});

		this.router.events.subscribe((routeEvent) => {
			if (routeEvent instanceof NavigationStart) {
				if (!this.wsSubscription && (routeEvent.url.includes('library/mediaclip') || routeEvent.url.includes('library/upload-transcode'))) {
					this.getJobs();
				} else if (this.wsSubscription && (!routeEvent.url.includes('library/mediaclip') && !routeEvent.url.includes('library/upload-transcode'))) {
					this.unsubscribeAll();
				}
			}
		});

		const doc = document as any;
		let hidden: string; let visibilityChange: string;
		if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
			hidden = 'hidden';
			visibilityChange = 'visibilitychange';
		} else if (typeof doc.msHidden !== 'undefined') {
			hidden = 'msHidden';
			visibilityChange = 'msvisibilitychange';
		} else if (typeof doc.webkitHidden !== 'undefined') {
			hidden = 'webkitHidden';
			visibilityChange = 'webkitvisibilitychange';
		}

		this.renderer = this.rendererFactory.createRenderer(null, null);

		this.renderer.listen('document', visibilityChange, (event: any) => {
			if (!document[hidden]) {
				this.getJobs();
			}
		});
	}

	unsubscribeAll() {
		this.webSocketsService.unsubscribeFromChannel('transcoding');
		this.wsSubscription = null;
	}

	get mediaclipJobDefinitionsSubject(): Subject <MediaclipJobDefinition[]>  {
		return this.mediaclipJobDefinitions;
	}

	get jobsSubject(): Subject <MediaclipJob[] > {
		return this._jobsSubject;
	}

	getJobs() {
		const today = new Date();
		today.setHours(0);
		today.setMinutes(0);

		const params = {finisheddate: today.toISOString(), force: 'true'};

		this.sapiService.getUrl('/transcoding', params).subscribe((mediaclipJobs: SapiQueryResponse) => {
			if (mediaclipJobs && mediaclipJobs.items) {
				this.mediaclipJobsArray = mediaclipJobs.items as MediaclipJob[];
				this._jobsSubject.next(this.mediaclipJobsArray);
			}

			if (!this.wsSubscription) {
				this.wsSubscription = this.webSocketsService.subscribeToChannel('transcoding').subscribe((websocketEvent: any) => {
					if (websocketEvent) {
						if (websocketEvent.ev === 'transcodingJobsCreated'
							&& websocketEvent.Object && websocketEvent.Object.id && websocketEvent.payload) {
							const newMediaclipJob = {
								id: websocketEvent.Object.id,
								mediajobs: websocketEvent.payload
							};
							this.mediaclipJobsArray.unshift(newMediaclipJob);
						} else if ((websocketEvent.ev === 'transcodingJobQueued' || websocketEvent.ev === 'transcodingJobStarted'
							|| websocketEvent.ev === 'transcodingJobDone' || websocketEvent.ev === 'transcodingFinished')
							&& websocketEvent.Object && websocketEvent.Object.id && websocketEvent.Object.id) {
							if (this.mediaclipJobsArray) {
								for (let i = 0; i < this.mediaclipJobsArray.length; i++) {
									if (this.mediaclipJobsArray[i].id === websocketEvent.Object.id) {
										const mediaclipJob = this.mediaclipJobsArray[i];
										if (websocketEvent.ev === 'transcodingFinished') {
											mediaclipJob.mediajobs =  websocketEvent.payload;
										} else {
											const updatedJobs = this.updateJobs(websocketEvent, mediaclipJob.mediajobs);
											mediaclipJob.mediajobs = updatedJobs;
										}
										this.mediaclipJobsArray.splice(i, 1, mediaclipJob);
									}
								}
							}
						}
						this._jobsSubject.next(this.mediaclipJobsArray);
					}
				});
			}
		});
	}

	getAverage(jobs: JobDetails[]) {

		let total = 0.0;
		jobs.forEach((job: any) => {
			if (job) {
				total += this.getPercentage(job);
			}
		});

		const percentage = total / jobs.length;

		return Number(percentage.toPrecision(2));
	}

	getPercentage(job: any): number {

		let percentage;

		if  (job && job.status === 'running') {
			percentage = job.timeleft / job.totaltime;
			percentage = Number(percentage.toPrecision(2));
			if (percentage > 1) {
				percentage = 1;
			} else if (percentage > 0.98) {
				percentage = 1;
			}
		} else if (job && (job.status === 'error' || job.status === 'transcoded')) {
			percentage = 1;
		} else {
			percentage = 0;
		}

		return percentage;
	}

	updateJobs(websocketEvent: any, jobs: JobDetails[]) {
		const event = websocketEvent.payload && websocketEvent.payload;
		if (event) {
			const updateJobs: JobDetails[] = jobs.filter((job: JobDetails) => job.mediajobdefid === event.mediajobdefid);

			if (updateJobs.length > 0) {
				const updateJob: JobDetails = updateJobs[0];
				updateJob.timeleft = event.timeleft;
				updateJob.totaltime = event.totaltime;
				updateJob.status = event.status;
			}

			return jobs;
		}
	}
}
